import { onCanvasLoadFinished, resetCanvasLoadTelemetryForTests } from './canvas-load-telemetry'
import { EditorDiagnostics, getEditorDiagnostics } from './tiptap-editor-diagnostics'
import { isStaticFirstRender } from './feature-flags'

/**
 * Document canvas load timing (canvas load optimization, task 0.2).
 * Records `performance.mark('canvas:<stage>')` for one load at a time, counts long tasks while it runs,
 * and keeps a report of the last load. Holds timings and counts only, never content.
 * Read it in DevTools with `window.__notchCanvasTiming()`.
 */

export type CanvasStage =
  | 'nav-start'
  | 'init-data-start'
  | 'init-data-end'
  | 'elements-request'
  | 'elements-response'
  | 'store-populated'
  | 'handlers-ready'
  | 'first-content-painted'
  | 'interactive'
  /** Chunked mounting (2.5) mounted the last block; recorded only when the list mounted in batches */
  | 'all-mounted'

export type CanvasLoadKind = 'route' | 'page-switch'

export type CanvasLoadReport = {
  kind: CanvasLoadKind
  /** false when the load was replaced or timed out before reaching `interactive` */
  complete: boolean
  /** First load since the page was opened: long tasks are counted from timeOrigin, not nav-start */
  hardLoad: boolean
  staticFirstRender: boolean
  /** ms since performance.timeOrigin */
  startedAt: number
  /** ms since performance.timeOrigin */
  stages: Partial<Record<CanvasStage, number>>
  /** ms since `startedAt` */
  sinceStart: Partial<Record<CanvasStage, number>>
  longTasks: { supported: boolean; count: number; totalMs: number; maxMs: number }
  editors: EditorDiagnostics
  domNodes: number
  /** The page produced no frame in time (background tab, hidden pane), so after-paint stages are upper bounds */
  paintEstimated: boolean
  /** The tab was hidden at some stage; timings are not comparable with a foreground load */
  hiddenDuringLoad: boolean
}

const STAGE_ORDER: CanvasStage[] = [
  'nav-start',
  'init-data-start',
  'init-data-end',
  'elements-request',
  'elements-response',
  'store-populated',
  'handlers-ready',
  'first-content-painted',
  'interactive',
  'all-mounted'
]
const MARK_PREFIX = 'canvas:'
/** Long tasks right after `interactive` (editor creation on nextTick, menus) still belong to the load */
export const SETTLE_MS = 2000
/**
 * A load that never reaches `interactive` (print mode, errors, navigating away) is closed after this.
 * Generous on purpose: the legacy one-editor-per-cell path takes well over a minute on a throttled CPU.
 */
export const ABANDON_MS = 180000
/** Hidden tabs and panes produce no animation frames; after-paint stages fall back to a timer after this */
export const PAINT_FALLBACK_MS = 1000

type ActiveLoad = {
  kind: CanvasLoadKind
  hardLoad: boolean
  startedAt: number
  stages: Partial<Record<CanvasStage, number>>
  longTaskFrom: number
  longTasks: { count: number; totalMs: number; maxMs: number }
  observer: PerformanceObserver | null
  timers: ReturnType<typeof setTimeout>[]
  idleScheduled: boolean
  /** The element list is mounting its tail in batches: the load settles after `all-mounted`, not `interactive` */
  awaitingAllMounted: boolean
  paintEstimated: boolean
  hiddenDuringLoad: boolean
}

function isDocumentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden'
}

let active: ActiveLoad | null = null
let lastReport: CanvasLoadReport | null = null
let anyLoadStarted = false
let measureNames: string[] = []

function now(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now()
}

function supportsLongTasks(): boolean {
  return (
    typeof PerformanceObserver !== 'undefined' && (PerformanceObserver.supportedEntryTypes || []).includes('longtask')
  )
}

function clearPerformanceEntries(): void {
  if (typeof performance === 'undefined') return
  try {
    STAGE_ORDER.forEach(stage => performance.clearMarks?.(MARK_PREFIX + stage))
    measureNames.forEach(name => performance.clearMeasures?.(name))
  } catch {
    // Instrumentation must never break a page load
  }
  measureNames = []
}

function observeLongTasks(load: ActiveLoad): PerformanceObserver | null {
  if (!supportsLongTasks()) return null
  try {
    const observer = new PerformanceObserver(list => {
      list.getEntries().forEach(entry => {
        if (entry.startTime < load.longTaskFrom) return
        load.longTasks.count++
        load.longTasks.totalMs += entry.duration
        load.longTasks.maxMs = Math.max(load.longTasks.maxMs, entry.duration)
      })
    })
    // buffered picks up the long tasks of a hard load that ran before this module was reached
    observer.observe({ type: 'longtask', buffered: true })
    return observer
  } catch {
    return null
  }
}

function stopLoad(load: ActiveLoad): void {
  load.timers.forEach(timer => clearTimeout(timer))
  load.timers = []
  load.observer?.disconnect()
  load.observer = null
}

function measureStages(load: ActiveLoad): void {
  if (typeof performance === 'undefined' || !performance.measure) return
  // Stages that race (handlers are wired before the list renders) are measured in the order they happened
  const recorded = STAGE_ORDER.filter(stage => load.stages[stage] !== undefined).sort(
    (a, b) => (load.stages[a] as number) - (load.stages[b] as number)
  )
  try {
    recorded.slice(1).forEach((stage, index) => {
      const name = `${MARK_PREFIX}${recorded[index]}..${stage}`
      performance.measure(name, MARK_PREFIX + recorded[index], MARK_PREFIX + stage)
      measureNames.push(name)
    })
    if (recorded.length > 1) {
      const name = `${MARK_PREFIX}total`
      performance.measure(name, MARK_PREFIX + recorded[0], MARK_PREFIX + recorded[recorded.length - 1])
      measureNames.push(name)
    }
  } catch {
    // A mark cleared by other code makes measure throw; the report still has the numbers
  }
}

function buildReport(load: ActiveLoad, complete: boolean): CanvasLoadReport {
  const sinceStart: Partial<Record<CanvasStage, number>> = {}
  STAGE_ORDER.forEach(stage => {
    const at = load.stages[stage]
    if (at !== undefined) sinceStart[stage] = at - load.startedAt
  })
  return {
    kind: load.kind,
    complete,
    hardLoad: load.hardLoad,
    staticFirstRender: isStaticFirstRender(),
    startedAt: load.startedAt,
    stages: { ...load.stages },
    sinceStart,
    longTasks: { supported: load.observer !== null, ...load.longTasks },
    editors: getEditorDiagnostics(),
    domNodes: typeof document === 'undefined' ? 0 : document.getElementsByTagName('*').length,
    paintEstimated: load.paintEstimated,
    hiddenDuringLoad: load.hiddenDuringLoad
  }
}

function finish(load: ActiveLoad, complete: boolean): void {
  if (active !== load) return
  // Deliver whatever the browser has queued but not yet handed to the callback
  const pending = load.observer?.takeRecords?.() || []
  pending.forEach(entry => {
    if (entry.startTime < load.longTaskFrom) return
    load.longTasks.count++
    load.longTasks.totalMs += entry.duration
    load.longTasks.maxMs = Math.max(load.longTasks.maxMs, entry.duration)
  })
  if (complete) measureStages(load)
  lastReport = buildReport(load, complete)
  stopLoad(load)
  active = null
  try {
    onCanvasLoadFinished(lastReport)
  } catch {
    // Telemetry must never affect the canvas
  }
  if (import.meta.env.VITE_SHOW_DEBUG_HELPERS === 'true') {
    console.info('[canvas-load]', lastReport)
  }
}

function afterPaint(load: ActiveLoad, callback: () => void): void {
  let done = false
  const run = (estimated: boolean) => {
    if (done) return
    done = true
    if (estimated) load.paintEstimated = true
    callback()
  }
  if (typeof requestAnimationFrame !== 'function') {
    setTimeout(() => run(false), 0)
    return
  }
  // rAF runs before the frame is painted; the task queued from it runs after
  requestAnimationFrame(() => setTimeout(() => run(false), 0))
  load.timers.push(setTimeout(() => run(true), PAINT_FALLBACK_MS))
}

function whenIdle(callback: () => void): void {
  // Safari has no requestIdleCallback
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(callback, { timeout: 1000 })
  else setTimeout(callback, 0)
}

/**
 * The public entry points run inside the router guard, the element store and the canvas components.
 * Instrumentation must never break navigation or a page load: an unexpected error only drops the current timing.
 */
function safely<A extends unknown[]>(fn: (...args: A) => void): (...args: A) => void {
  return (...args: A) => {
    try {
      fn(...args)
    } catch {
      try {
        if (active) stopLoad(active)
      } catch {
        // Nothing left to clean up safely
      }
      active = null
    }
  }
}

const finishSafely = safely(finish)

function startLoad(kind: CanvasLoadKind): void {
  if (active) finish(active, false)
  clearPerformanceEntries()
  const startedAt = now()
  const hardLoad = !anyLoadStarted
  anyLoadStarted = true
  const load: ActiveLoad = {
    kind,
    hardLoad,
    startedAt,
    stages: {},
    longTaskFrom: hardLoad ? 0 : startedAt,
    longTasks: { count: 0, totalMs: 0, maxMs: 0 },
    observer: null,
    timers: [],
    idleScheduled: false,
    awaitingAllMounted: false,
    paintEstimated: false,
    hiddenDuringLoad: false
  }
  active = load
  load.observer = observeLongTasks(load)
  load.timers.push(setTimeout(() => finishSafely(load, false), ABANDON_MS))
  mark('nav-start')
}

function mark(stage: CanvasStage): void {
  const load = active
  if (!load || load.stages[stage] !== undefined) return
  load.stages[stage] = now()
  if (isDocumentHidden()) load.hiddenDuringLoad = true
  try {
    performance.mark?.(MARK_PREFIX + stage)
  } catch {
    // The report keeps the timestamp even if the mark could not be written
  }

  const { stages } = load
  if (!load.idleScheduled && stages['first-content-painted'] !== undefined && stages['handlers-ready'] !== undefined) {
    // Interactive: content is painted, input handlers are wired, and the main thread is free again
    load.idleScheduled = true
    whenIdle(() => {
      if (active === load) markCanvas('interactive')
    })
  }
  if (stage === 'all-mounted') load.awaitingAllMounted = false
  const settles =
    (stage === 'interactive' && !load.awaitingAllMounted) ||
    (stage === 'all-mounted' && stages.interactive !== undefined)
  if (settles) {
    load.timers.push(setTimeout(() => finishSafely(load, true), SETTLE_MS))
  }
}

/** Identifies the load a chunked list expects to mark; any other load ignores it */
export type AllMountedToken = object | null

/**
 * The element list mounts in chunks: keep the load open until `all-mounted` so the batches are measured too.
 * Returns a token for this load; a list from an earlier load (still mounted during a route change) can then never
 * mark or hold open the new one.
 */
export function expectAllMountedMark(): AllMountedToken {
  try {
    const load = active
    if (!load || load.stages['all-mounted'] !== undefined) return null
    load.awaitingAllMounted = true
    return load
  } catch {
    return null
  }
}

/** The chunked list finished mounting: marks `all-mounted` on the load that expected it, if it is still running */
export const markAllMounted = safely((token: AllMountedToken) => {
  if (token && token === active) mark('all-mounted')
})

/** The chunked list went away (destroyed, or a new page load) before mounting everything: let its load settle */
export const releaseAllMountedMark = safely((token: AllMountedToken) => {
  const load = active
  if (!token || token !== load || !load.awaitingAllMounted) return
  load.awaitingAllMounted = false
  if (load.stages.interactive !== undefined) load.timers.push(setTimeout(() => finishSafely(load, true), SETTLE_MS))
})

/** Starts timing a new canvas load, discarding any unfinished one. */
export const startCanvasLoad = safely(startLoad)

/**
 * Starts a load unless one was just started and has not requested elements yet,
 * so a route change that also reloads the page keeps its router nav-start.
 */
export const ensureCanvasLoad = safely((kind: CanvasLoadKind) => {
  if (active && active.stages['elements-request'] === undefined) return
  startLoad(kind)
})

/** Records a stage of the active load, once. Does nothing while no load is being timed. */
export const markCanvas = safely(mark)

/** Records a stage after the browser has painted the current DOM changes. */
export const markCanvasAfterPaint = safely((stage: CanvasStage) => {
  const load = active
  if (!load) return
  afterPaint(load, () => {
    if (active === load) markCanvas(stage)
  })
})

export function isCanvasLoadActive(): boolean {
  return active !== null
}

/** The last finished load (complete or abandoned), or null before the first one finishes. */
export function getCanvasLoadReport(): CanvasLoadReport | null {
  return lastReport
}

/** Test helper: forgets all loads, including whether one has run since page load. */
export function resetCanvasLoadTiming(): void {
  if (active) stopLoad(active)
  active = null
  lastReport = null
  anyLoadStarted = false
  clearPerformanceEntries()
  resetCanvasLoadTelemetryForTests()
}

if (typeof window !== 'undefined') {
  window.__notchCanvasTiming = getCanvasLoadReport
}
